import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import DefaultInterfacesHandler from '../handlers/DefaultInterfacesHandler';

import { DateTime } from 'luxon';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';

import Container from '@mui/material/Container';
import Badge from '@mui/material/Badge';

import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { AnimatePresence, motion } from 'framer-motion';
import DialogContent from '@mui/material/DialogContent';
import PureListView, { SMALL_CONTAINER_THRESHOLD_PX } from './PureListView';
import ProductSelectorButton from '../base/ProductSelectorButton';
import { CrossFade } from '../base/CrossFade';
import { LOADING_GRACE_MS, LOADING_MIN_DISPLAY_MS } from '../../hooks/useSmartLoading';
import NoBookables from '../base/NoBookables';
import ClosableDialogTitle from '../base/ClosableDialogTitle';
import ScopableDialog from '../base/ScopableDialog';

import { useTranslation } from 'react-i18next';
import usePropsBeforeContext from '../../hooks/usePropsBeforeContext';
import { CalendarViewContext } from '../Calendar';

import MotionGrid from '../base/MotionGrid';

import useElDimensions from '../../hooks/useElDimensions';

import useCurrentLanguage from '../../hooks/useCurrentLanguage';

const EASE_DECEL = [0.22, 0.1, 0, 1];
const LIST_MIN_WIDTH = 280;
const DATE_CALENDAR_WIDTH = 320;

// Derive a stable key for the current list data (e.g., a version or hash of ids)
function useListKey(slots) {
  // simplest: join ids; replace with your own signature
  if (!slots) return undefined;

  return []
    .concat(...Object.values(slots))
    .map((slot) => slot?.uuid)
    .join(',');
}

function isSelectedDay(day, highlightedDays = []) {
  if (day <= DateTime.now()) return false;

  for (const [date, timeslots] of Object.entries(highlightedDays)) {
    if (date === day.toFormat('yyyy-MM-dd') && timeslots?.length > 0) {
      return true;
    }
  }
  return false;
}

function ServerDay(props) {
  const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

  const isSelected = useMemo(() => {
    if (outsideCurrentMonth) return false;

    return isSelectedDay(day, highlightedDays);
  }, [highlightedDays, day, outsideCurrentMonth]);

  return (
    <Badge
      key={props.day.toString()}
      variant="dot"
      color="primary"
      overlap="circular"
      invisible={!isSelected}
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
      />
    </Badge>
  );
}

export default function EyeCandy(props) {
  const [selectedDate, setSelectedDate] = useState(null);
  const hasAutoSelected = useRef(false);

  const { t } = useTranslation();
  const currentLanguage = useCurrentLanguage();
  const {
    isFetching,
    bookables,
    products,
    product,
    inDetailsMode,
    isViewportSmall,
    openProductPage,
    height,
    constrainDialogsToContainer,
    suppressDefaultInterfaces = false,
  } = usePropsBeforeContext(props, CalendarViewContext);

  const [prdBtnRef, prdBtnDimensions] = useElDimensions(undefined, 'outer');
  const [eyeCandyRef, eyeCandyDimensions] = useElDimensions(undefined, 'inner');

  // Determine whether EyeCandy uses mobile layout (calendar only, list in dialog)
  // or desktop layout (calendar + list side-by-side).
  // When DetailsView says mobile, EyeCandy follows suit.
  // Otherwise, EyeCandy decides based on its own container width.
  const isMobileLayout = useMemo(() => {
    if (inDetailsMode && isViewportSmall) return true;
    if (!eyeCandyDimensions.width) return false;
    return eyeCandyDimensions.width < DATE_CALENDAR_WIDTH + LIST_MIN_WIDTH;
  }, [inDetailsMode, isViewportSmall, eyeCandyDimensions.width]);

  // When in desktop mode, compute whether PureListView should use compact layout.
  // EyeCandy knows its own width and how much DateCalendar takes, so it can
  // reliably determine the remaining space — unlike PureListView which sits inside
  // an animating container whose measured width may be stale.
  const listForceSmallContainer = useMemo(() => {
    if (isMobileLayout || !eyeCandyDimensions.width) return undefined;
    const listWidth = eyeCandyDimensions.width - DATE_CALENDAR_WIDTH;
    return listWidth < SMALL_CONTAINER_THRESHOLD_PX;
  }, [isMobileLayout, eyeCandyDimensions.width]);

  const currentBookables = useMemo(() => {
    for (const [date, timeslots] of Object.entries(bookables)) {
      if (
        date === selectedDate?.toFormat('yyyy-MM-dd') &&
        timeslots?.length > 0
      ) {
        const result = {};
        result[date] = timeslots;
        return result;
      }
    }
  }, [bookables, selectedDate]);

  const clearSelection = useCallback(() => setSelectedDate(null), []);

  const shouldDisableDate = useCallback(
    (day) => {
      return !isSelectedDay(day, bookables);
    },
    [bookables],
  );

  // Auto-select first day with bookables on initial load (desktop only)
  useEffect(() => {
    if (
      isMobileLayout ||
      hasAutoSelected.current ||
      isFetching ||
      !bookables ||
      Object.keys(bookables).length === 0
    ) {
      return;
    }

    // Get sorted dates and find the first one after today
    const sortedDates = Object.keys(bookables).sort();
    const today = DateTime.now().toFormat('yyyy-MM-dd');

    const firstAvailableDate =
      sortedDates.find((date) => date > today) || sortedDates[0];

    if (firstAvailableDate) {
      setSelectedDate(DateTime.fromISO(firstAvailableDate));
      hasAutoSelected.current = true;
    }
  }, [bookables, isFetching, isMobileLayout]);

  const listKey = useListKey(currentBookables);

  const showNoBookables = useMemo(() => {
    return (
      !isFetching &&
      product &&
      products?.length > 0 &&
      Object.keys(bookables || {}).length === 0
    );
  }, [isFetching, product, products, bookables]);

  return (
    <Container
      maxWidth={inDetailsMode ? 'xl' : 'md'}
      sx={{ p: inDetailsMode ? '0 !important' : undefined }}
    >
      <Grid
        ref={eyeCandyRef}
        id={'eye-candy'}
        container
        layout
        transition={{ duration: 0.5 }}
        sx={{
          justifyContent:
            inDetailsMode && !isMobileLayout ? 'flex-start' : 'center',
          alignContent: 'flex-start',
          height: isMobileLayout ? 'auto' : height,
        }}
      >
        {openProductPage && (
          <Grid
            container
            item
            xs={12}
            sx={{
              justifyContent:
                inDetailsMode && !isMobileLayout ? 'flex-start' : 'center',
              mt: 1,
              mb: 0.5,
            }}
          >
            <Grid
              item
              xs="auto"
              sx={{
                alignSelf: 'flex-end',
              }}
            >
              <CrossFade
                showSkeleton={isFetching || (!product && products?.length > 0)}
                graceMs={LOADING_GRACE_MS}
                minDisplayMs={LOADING_MIN_DISPLAY_MS}
                skeleton={
                  <Skeleton variant="rounded" height="36px" width="160px" />
                }
                height={'40px'}
                wrapperProps={{ sx: { width: 'fit-content' } }}
              >
                <ProductSelectorButton
                  ref={prdBtnRef}
                  openProductPage={openProductPage}
                  product={product}
                  products={products}
                  wrapperProps={{ sx: { width: 'inherit' } }}
                  buttonProps={{
                    fullWidth: isMobileLayout,
                  }}
                >
                  {product?.name || t('product_selection_headline')}
                </ProductSelectorButton>
              </CrossFade>
            </Grid>
          </Grid>
        )}
        <MotionGrid
          layout="position"
          container
          item
          xs={12}
          sm="auto"
          sx={{ overflow: isMobileLayout ? 'visible' : undefined }}
        >
          <LocalizationProvider
            dateAdapter={AdapterLuxon}
            adapterLocale={currentLanguage?.tag || 'de'}
            timezone="system"
          >
            <DateCalendar
              referenceDate={DateTime.now()}
              loading={isFetching || (!product && products?.length > 0)}
              disableHighlightToday
              renderLoading={() => <DayCalendarSkeleton />}
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
              }}
              views={['day']}
              view="day"
              maxDate={DateTime.now().plus({ months: 3 })} // maximum the backend returns
              minDate={DateTime.now().minus({ days: 1 })} // minimum the backend returns
              shouldDisableDate={shouldDisableDate}
              slots={{
                day: ServerDay,
              }}
              slotProps={{
                day: {
                  highlightedDays: bookables,
                },
              }}
              sx={{
                ml: isMobileLayout ? 'auto' : '-15px',
                mr: isMobileLayout ? 'auto' : undefined,
                height: 'auto',
                ...(isMobileLayout && { pb: 0 }),
                '& .MuiPickersCalendarHeader-root': {
                  mt: '5px',
                },
              }}
            />
          </LocalizationProvider>
        </MotionGrid>

        {/* Desktop: animated width column for list / no-bookables */}
        {!isMobileLayout && (
          <AnimatePresence initial={false} mode="wait">
            {(currentBookables || showNoBookables) && (
              <motion.div
                key={currentBookables ? 'list' : 'no-bookables'}
                initial={
                  inDetailsMode
                    ? { maxWidth: 0, opacity: 0 }
                    : { width: 0, opacity: 0 }
                }
                animate={
                  inDetailsMode
                    ? { maxWidth: 500, opacity: 1 }
                    : { width: 'auto', opacity: 1 }
                }
                exit={
                  inDetailsMode
                    ? { maxWidth: 0, opacity: 0 }
                    : { width: 0, opacity: 0 }
                }
                transition={{ duration: 0.4, ease: EASE_DECEL }}
                style={{
                  overflow: 'hidden',
                  ...(inDetailsMode
                    ? { flexGrow: 1, flexShrink: 1, flexBasis: 0 }
                    : { flexShrink: 0 }),
                }}
              >
                <div
                  style={{
                    minWidth: inDetailsMode ? 0 : LIST_MIN_WIDTH,
                  }}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {currentBookables ? (
                      <motion.div
                        key={listKey}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{
                          duration: 0.26,
                          ease: EASE_DECEL,
                        }}
                      >
                        <PureListView
                          height={height - prdBtnDimensions?.height}
                          suppressDefaultInterfaces
                          openProductPage={undefined}
                          openResourcePage={undefined}
                          bookables={currentBookables}
                          forceSmallContainer={listForceSmallContainer}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="no-bookables"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <NoBookables />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Small screen: dialog for timeslot list */}
        {isMobileLayout && (
          <ScopableDialog
            open={!!currentBookables}
            onClose={clearSelection}
            enableScoping={constrainDialogsToContainer}
            fullScreen
          >
            <ClosableDialogTitle
              position={constrainDialogsToContainer ? 'left' : 'right'}
              onClose={clearSelection}
            />
            <DialogContent>
              <Container maxWidth="sm" sx={{ mt: 1.5 }}>
                <PureListView
                  suppressDefaultInterfaces
                  openProductPage={undefined}
                  openResourcePage={undefined}
                  bookables={currentBookables}
                />
              </Container>
            </DialogContent>
          </ScopableDialog>
        )}

        {/* Small screen: no bookables */}
        <AnimatePresence>
          {isMobileLayout && showNoBookables && (
            <MotionGrid
              item
              xs
              sm
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <NoBookables />
            </MotionGrid>
          )}
        </AnimatePresence>
        {!suppressDefaultInterfaces && <DefaultInterfacesHandler />}
      </Grid>
    </Container>
  );
}
